import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { addService, getServices, deleteService, addCategory, getCategories, subscribeServices } from '../firebase/firestore';
import { addToPendingSync, syncPendingServices } from '../services/syncService';
import { Service } from '../types';
import { formatCurrency } from '../utils/helpers';
import LoadingOverlay from '../components/LoadingOverlay';
import AdsSection from '../components/AdsSection';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faArrowLeft, 
  faPlus, 
  faTrash, 
  faArrowTrendUp, 
  faArrowTrendDown, 
  faUser, 
  faCalendar,
  faDollarSign,
  faChartLine,
  faPercent,
  faTag,
  faStickyNote,
  faRotate,
  faMoneyBillTransfer,
  faMoneyBillTrendUp
} from '@fortawesome/free-solid-svg-icons';
import '../styles/Services.css';

const Services: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [services, setServices] = useState<Service[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [description, setDescription] = useState('');
  const [client, setClient] = useState('');
  const [value, setValue] = useState('');
  const [profitMargin, setProfitMargin] = useState('30');
  const [useCustomMargin, setUseCustomMargin] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  // Campos
  const [serviceType, setServiceType] = useState<'receita' | 'despesa'>('receita');
  const [dateStr, setDateStr] = useState<string>(() => new Date().toISOString().slice(0,10)); // YYYY-MM-DD
  const [category, setCategory] = useState('');
  const [notes, setNotes] = useState('');
  const [recurrence, setRecurrence] = useState<'única' | 'mensal' | 'anual'>('única');
  const [paymentMethod, setPaymentMethod] = useState('');
  const [syncErrorIds, setSyncErrorIds] = useState<string[]>([]); // IDs dos serviços não sincronizados
  // Categories autocomplete
  const [categories, setCategories] = useState<string[]>([]);
  const [categorySuggestions, setCategorySuggestions] = useState<string[]>([]);
  const [showCategorySuggestions, setShowCategorySuggestions] = useState(false);

  useEffect(() => {
    // Carrega instantaneamente do cache local
    // Fluxo para atualizar (sem cache local, via Firestore)
    const fetchAndSync = async () => {
      if (!user) return;
      try {
        const data = await getServices(user.uid);
        setServices(data);
        setPageLoading(false);

        // carrega categorias do usuário
        try {
          const cats = await getCategories(user.uid);
          setCategories(cats);
        } catch (err) {
          console.warn('Erro ao carregar categorias:', err);
        }

      } catch (error) {
        console.error('Erro ao sincronizar com nuvem:', error);
      }
    };
    fetchAndSync();

    // Subscrição em tempo real para refletir alterações em outros navegadores
    let unsubscribeServices: (() => void) | undefined;
    if (user) {
      unsubscribeServices = subscribeServices(user.uid, (data) => {
        setServices(data);
        setPageLoading(false);
      }, (err) => {
        console.warn('Falha no snapshot de serviços:', err);
      });
    }

    // Sincroniza pendências locais antes de qualquer coisa
    if (user) {
      syncPendingServices(user.uid)
        .catch(err => console.warn('Falha ao sincronizar pendências locais:', err));
    }

    // Sincroniza automaticamente ao voltar online
    const handleOnline = () => {
      if (user) {
        syncPendingServices(user.uid)
          .catch(err => console.warn('Falha ao sincronizar pendências locais:', err));
      }
      fetchAndSync();
    };
    window.addEventListener('online', handleOnline);

    return () => {
      if (unsubscribeServices) unsubscribeServices();
      window.removeEventListener('online', handleOnline);
    };
  }, [user]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !description || !value || !profitMargin) return;

    setLoading(true);
    try {
      const valueNum = parseFloat(value);
      const marginNum = parseFloat(profitMargin);
      // Parse date from the yyyy-mm-dd input
      const parsedDate = dateStr ? new Date(dateStr + 'T00:00:00') : new Date();
      // Campos detalhados (disponíveis para todos)
      const detailedFields = {
        type: serviceType,
        category: category || undefined,
        notes: notes || undefined,
        recurrence: recurrence || 'única',
        paymentMethod: paymentMethod || undefined,
      };
      const newService = {
        description,
        client: client || undefined,
        value: valueNum,
        profitMargin: marginNum,
        date: parsedDate,
        ...detailedFields
      };

      // Limpa form e fecha modal ANTES de salvar
      const tempId = `temp-${Date.now()}`;

      setDescription('');
      setClient('');
      setValue('');
      setProfitMargin('30');
      setUseCustomMargin(false);
      setShowForm(false);
      setCategory('');
      setNotes('');
      setServiceType('receita');
      setPaymentMethod('');
      setDateStr(new Date().toISOString().slice(0,10));
      setRecurrence('única');

      // Salva no Firestore (aguarda e realiza sync offline quando precisar)
      try {
        await addService(user.uid, newService);

        // Se a categoria não existe, tenta adicionar
        if (newService.category && !categories.includes(newService.category)) {
          try {
            await addCategory(user.uid, newService.category);
            const cats = await getCategories(user.uid);
            setCategories(cats);
          } catch (e) {
            console.warn('Não foi possível salvar categoria automaticamente', e);
          }
        }

        // Força nova leitura para manter consistência completa
        const freshData = await getServices(user.uid);
        setServices(freshData);

        // Tenta sincronizar itens pendentes (se existirem)
        await syncPendingServices(user.uid);
      } catch (err: any) {
        console.warn('❌ Erro ao salvar no Firebase (offline ou falha):', err);

        // Salva na fila de pendência local para tentar novamente quando voltar online
        addToPendingSync({
          tempId,
          service: newService,
          timestamp: Date.now()
        });

        setSyncErrorIds(ids => [...ids, tempId]);

        // Tenta manter a lista de serviços do servidor atualizada (mesmo que offline)
        try {
          const pendingData = await getServices(user.uid);
          setServices(pendingData);
        } catch {
          // caso não consiga carregar agora, mantemos o estado atual
        }
      }
    } catch (error) {
      console.error('Erro:', error);
      alert('Erro ao processar serviço. Tente novamente.');
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Deseja realmente excluir este serviço?')) {
      return;
    }

    // Armazena o serviço para possível restauração
    const deletedService = services.find(s => s.id === id);
    
    try {
      // Remove da UI imediatamente (otimista)
      const updatedServices = services.filter(s => s.id !== id);
      setServices(updatedServices);
      
      // Deleta no Firebase
      await deleteService(id);
      
    } catch (error) {
      console.error('❌ Erro ao deletar:', error);
      alert('Erro ao deletar serviço.');
      
      // Restaura o serviço em caso de erro
      if (deletedService) {
        setServices(prev => [deletedService, ...prev].sort((a, b) => 
          b.date.getTime() - a.date.getTime()
        ));
      }
    }
  };

  return (
    <div className="services-page">
      <LoadingOverlay show={pageLoading} message="Carregando serviços..." />
      
      <header className="page-header">
        <button onClick={() => navigate('/dashboard')} className="back-btn">
          <FontAwesomeIcon icon={faArrowLeft} /> Voltar
        </button>
        <h1>
          <FontAwesomeIcon icon={faMoneyBillTrendUp} /> Lançamentos Financeiros
        </h1>
        <button onClick={() => setShowForm(true)} className="add-btn">
          <FontAwesomeIcon icon={faPlus} />
        </button>
      </header>

      {syncErrorIds.length > 0 && (
        <div style={{ margin: '10px 0 18px' }}>
          <div className="error-banner" style={{ background: '#fee', border: '1px solid #f88', borderRadius: 8, padding: 10, color: '#c33' }}>
            {syncErrorIds.length} lançamento(s) com erro de sincronização. <button onClick={() => { navigator.clipboard.writeText(syncErrorIds.join(', ')); alert('IDs copiados para a área de transferência'); }} style={{ marginLeft: 8 }} className="secondary">Copiar IDs</button>
          </div>
        </div>
      )}

      {showForm && (
        <div className="modal-overlay" onClick={() => setShowForm(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                  <h2>Novo Lançamento</h2>
            <form onSubmit={handleSubmit}>
              <div className="premium-fields" style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                <div style={{ flex: '1 1 160px' }}>
                  <label><FontAwesomeIcon icon={faMoneyBillTrendUp} /> Tipo de movimentação</label>
                  <select value={serviceType} onChange={e => setServiceType(e.target.value as 'receita' | 'despesa')} required>
                    <option value="receita">Receita (Entrada)</option>
                    <option value="despesa">Despesa (Saída)</option>
                  </select>
                </div>
                <div style={{ flex: '1 1 140px' }}>
                  <label>Data do lançamento</label>
                  <input type="date" value={dateStr} onChange={e => setDateStr(e.target.value)} required />
                </div>
              </div>

              <input
                type="text"
                placeholder="Descrição do lançamento *"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                required
              />
              <input
                type="text"
                placeholder="Contraparte / Cliente (opcional)"
                value={client}
                onChange={(e) => setClient(e.target.value)}
              />
              <input
                type="number"
                step="0.01"
                placeholder="Valor (R$) *"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                required
              />

              {/* Campos dependentes do tipo de movimentação */}
              {serviceType === 'receita' && (
                <div className="margin-selector">
                  <label>Margem de Lucro:</label>
                  {!useCustomMargin ? (
                    <>
                      <select
                        value={profitMargin}
                        onChange={(e) => setProfitMargin(e.target.value)}
                        required
                      >
                        {Array.from({ length: 21 }, (_, i) => i * 5).map(value => (
                          <option key={value} value={value}>
                            {value}%
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onClick={() => setUseCustomMargin(true)}
                        className="custom-btn"
                      >
                        Digitar valor
                      </button>
                    </>
                  ) : (
                    <>
                      <input
                        type="number"
                        step="0.1"
                        min="0"
                        max="100"
                        placeholder="Digite a margem (%)"
                        value={profitMargin}
                        onChange={(e) => setProfitMargin(e.target.value)}
                        required
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setUseCustomMargin(false);
                          setProfitMargin('30');
                        }}
                        className="custom-btn"
                      >
                        Usar lista
                      </button>
                    </>
                  )}
                </div>
              )}

              {serviceType === 'despesa' && (
                <div className="premium-fields">
                  <label>Método de Pagamento:</label>
                  <input type="text" placeholder="Ex: Cartão, Transferência, Dinheiro" value={paymentMethod} onChange={e => setPaymentMethod(e.target.value)} />
                </div>
              )}
              {/* Categoria e campos base sempre disponíveis; observação/recorrência agora para todos */}
              <div className="premium-fields" style={{ position: 'relative' }}>
                <label>
                  <FontAwesomeIcon icon={faTag} /> Categoria:
                </label>
                <input
                  type="text"
                  placeholder="Ex: Serviços, Produtos, Consultoria"
                  value={category}
                  onChange={e => {
                    setCategory(e.target.value);
                    const q = e.target.value.toLowerCase();
                    if (!q) { setCategorySuggestions([]); setShowCategorySuggestions(false); return; }
                    const matches = categories.filter(c => c.toLowerCase().includes(q)).slice(0,6);
                    setCategorySuggestions(matches);
                    setShowCategorySuggestions(matches.length > 0);
                  }}
                  onFocus={() => { if (categories.length) { setCategorySuggestions(categories.slice(0,6)); setShowCategorySuggestions(true); }} }
                  onBlur={() => setTimeout(() => setShowCategorySuggestions(false), 150)}
                />
                {showCategorySuggestions && categorySuggestions.length > 0 && (
                  <ul className="autocomplete-list">
                    {categorySuggestions.map(s => (
                      <li key={s} className="autocomplete-item" onMouseDown={() => { setCategory(s); setShowCategorySuggestions(false); }}>{s}</li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="premium-fields">
                <label>
                  <FontAwesomeIcon icon={faStickyNote} /> Observação:
                </label>
                <input type="text" placeholder="Notas adicionais sobre este lançamento" value={notes} onChange={e => setNotes(e.target.value)} />
              </div>
              <div className="premium-fields">
                <label>
                  <FontAwesomeIcon icon={faRotate} /> Recorrência:
                </label>
                <select value={recurrence} onChange={e => setRecurrence(e.target.value as 'única' | 'mensal' | 'anual')}>
                  <option value="única">Única</option>
                  <option value="mensal">Mensal</option>
                  <option value="anual">Anual</option>
                </select>
              </div>
              <div className="form-actions">
                <button type="button" onClick={() => setShowForm(false)} className="cancel-btn">
                  Cancelar
                </button>
                <button type="submit" disabled={loading} className="submit-btn">
                  {loading ? 'Salvando...' : 'Salvar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="services-list">
        {services.length === 0 ? (
          <div className="empty-state">
            <FontAwesomeIcon icon={faMoneyBillTransfer} size="3x" style={{color: '#3b82f6', marginBottom: '1rem'}} />
            <p>Nenhum serviço cadastrado ainda</p>
            <button onClick={() => setShowForm(true)} className="empty-btn">
              <FontAwesomeIcon icon={faPlus} /> Adicionar Primeiro Lançamento
            </button>
          </div>
        ) : (
          services.map(service => (
            <div key={service.id} className="service-card">
              <div className="service-header">
                <div>
                  <h3>
                    {service.type && (
                      <FontAwesomeIcon 
                        icon={service.type === 'receita' ? faArrowTrendUp : faArrowTrendDown} 
                        style={{color: service.type === 'receita' ? '#10b981' : '#ef4444', marginRight: '0.5rem'}}
                      />
                    )}
                    {service.description}
                  </h3>
                  {service.client && (
                    <p className="client-name">
                      <FontAwesomeIcon icon={faUser} /> {service.client}
                    </p>
                  )}
                </div>
                <button onClick={() => handleDelete(service.id)} className="delete-btn">
                  <FontAwesomeIcon icon={faTrash} />
                </button>
              </div>
              <div className="service-details">
                <div className="detail">
                  <span><FontAwesomeIcon icon={faDollarSign} /> Valor:</span>
                  <strong>{formatCurrency(service.value)}</strong>
                </div>
                <div className="detail">
                  <span><FontAwesomeIcon icon={faChartLine} /> Lucro:</span>
                  <strong className="green">
                    {formatCurrency(service.value * (service.profitMargin / 100))}
                  </strong>
                </div>
                <div className="detail">
                  <span><FontAwesomeIcon icon={faPercent} /> Margem:</span>
                  <strong>{service.profitMargin.toFixed(1)}%</strong>
                </div>
                {service.type && (
                  <div className="detail">
                    <span><FontAwesomeIcon icon={faMoneyBillTrendUp} /> Tipo:</span>
                    <strong className={service.type === 'receita' ? 'type-receita' : 'type-despesa'}>
                      <FontAwesomeIcon 
                        icon={service.type === 'receita' ? faArrowTrendUp : faArrowTrendDown} 
                      />
                      {' '}{service.type === 'receita' ? 'Receita' : 'Despesa'}
                    </strong>
                  </div>
                )}
                {service.category && (
                  <div className="detail">
                    <span><FontAwesomeIcon icon={faTag} /> Categoria:</span>
                    <strong>{service.category}</strong>
                  </div>
                )}
                {service.notes && (
                  <div className="detail">
                    <span><FontAwesomeIcon icon={faStickyNote} /> Obs.:</span>
                    <strong>{service.notes}</strong>
                  </div>
                )}
                {service.recurrence && (
                  <div className="detail">
                    <span><FontAwesomeIcon icon={faRotate} /> Recorrência:</span>
                    <strong>{service.recurrence.charAt(0).toUpperCase() + service.recurrence.slice(1)}</strong>
                  </div>
                )}
              </div>
              <div className="service-footer">
                <small>
                  <FontAwesomeIcon icon={faCalendar} /> {new Date(service.date).toLocaleDateString('pt-BR')}
                </small>
              </div>
            </div>
          ))
        )}
      </div>

      <AdsSection />
    </div>
  );
};

export default Services;
